import { dayLookup, payForDay, splitHoursOn, unworkedHolidays } from "@core";
import type { EmploymentTerms, Shift, UnworkedHoliday } from "@core";

import { supabase } from "./supabase";

/**
 * One person's month, as the attendance register South African law requires an
 * employer to keep.
 *
 * Times are formatted in the **household's** timezone, from orgs.timezone, not
 * the browser's. An employer reading this from another country would otherwise
 * see clock-ins an hour or two out on a record they may one day have to stand
 * behind, and nothing on the page would say why.
 *
 * Each shift is priced against the terms it was worked under. Using today's
 * rate would restate history — the register, not only the money.
 */

export type RegisterStatus =
  | "open"
  | "auto-closed"
  | "flagged"
  | "overtime-undecided"
  | "complete";

export interface RegisterRow {
  id: string;
  /** Calendar date in the household's timezone — the day the shift began. */
  date: string;
  clockIn: string;
  clockOut: string | null;
  hours: number;
  overtime: number;
  wages: number;
  status: RegisterStatus;
}

export interface RegisterMonth {
  rows: RegisterRow[];
  hours: number;
  overtime: number;
  wages: number;
  /** Shifts that have been closed off. An open shift is not a completed one. */
  completed: number;
  /**
   * Public holidays the Act pays for and the register has no shift to show.
   *
   * BCEA s18(2)(a). Without these a week with a blank Thursday and a full pay
   * figure looks like an error, and the day somebody was entitled to stay home
   * on looks like a day they did not turn up.
   */
  holidays: UnworkedHoliday[];
}

interface ShiftRow {
  id: string;
  terms_id: string;
  clock_in: string;
  clock_out: string | null;
  flag: string | null;
  overtime_status: string | null;
  auto_closed_at: string | null;
}

const pad = (n: number) => String(n).padStart(2, "0");

/** The wall-clock date in a given zone, as YYYY-MM-DD. */
export function zonedDate(ms: number, timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(ms));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

/** The wall-clock time in a given zone, as HH:MM. */
export function zonedTime(ms: number, timeZone: string): string {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(ms));
}

function statusOf(row: ShiftRow, overtime: number): RegisterStatus {
  if (!row.clock_out) return "open";
  if (row.flag) return "flagged";
  // Shifty guessed the leaving time from the roster because nobody tapped out.
  // Every screen showing it has to say so — an assumed time presented as a
  // recorded one is the thing that makes a register worthless in a dispute.
  if (row.auto_closed_at) return "auto-closed";
  if (overtime > 0 && !row.overtime_status) return "overtime-undecided";
  return "complete";
}

export async function loadRegister(
  employeeId: string,
  year: number,
  /** 1-12, as a person would say it. */
  month: number,
  timeZone: string,
): Promise<RegisterMonth> {
  // A day either side, because the month's edges in the household's timezone do
  // not line up with UTC. The rows are filtered by their zoned date afterwards,
  // so the padding only ever adds candidates, never wrong ones.
  const from = new Date(Date.UTC(year, month - 1, 0)).toISOString();
  const to = new Date(Date.UTC(year, month, 2)).toISOString();
  const prefix = `${year}-${pad(month)}`;
  const first = `${prefix}-01`;
  const last = `${prefix}-${pad(new Date(Date.UTC(year, month, 0)).getUTCDate())}`;

  const [shiftRes, termRes, patternRes, leaveRes] = await Promise.all([
    supabase
      .from("shifts")
      .select("id, terms_id, clock_in, clock_out, flag, overtime_status, auto_closed_at")
      .eq("employee_id", employeeId)
      .gte("clock_in", from)
      .lt("clock_in", to)
      .order("clock_in", { ascending: false })
      .returns<ShiftRow[]>(),
    supabase.from("employment_terms").select("*").eq("employee_id", employeeId),
    // The register used to skip this and take dayLookup's safe default. That
    // was defensible while it only priced worked days; it is not, now that it
    // has to decide which public holidays somebody was entitled to be paid for
    // without working. s18(2) is about days they "would ordinarily work".
    supabase.from("work_pattern").select("weekday").eq("employee_id", employeeId),
    // A holiday inside approved leave is paid as leave — s20(8) hands the leave
    // day back rather than paying twice — so it must not appear here as well.
    supabase.from("leave_requests").select("starts_on, ends_on")
      .eq("employee_id", employeeId).eq("status", "approved"),
  ]);

  const terms = new Map<string, EmploymentTerms>();
  for (const t of (termRes.data ?? []) as Record<string, any>[]) {
    terms.set(t.id, {
      id: t.id,
      employeeId: t.employee_id,
      validFrom: t.valid_from,
      validTo: t.valid_to,
      rate: t.rate_cents / 100,
      currency: t.currency,
      ordinaryDaily: Number(t.ordinary_daily),
      ordinaryWeekly: Number(t.ordinary_weekly),
      breakMins: t.break_mins,
      breakAfter: Number(t.break_after_h),
      ot: Number(t.ot_multiplier),
      sunday: Number(t.sunday_multiplier),
      holiday: Number(t.holiday_multiplier ?? 2),
      daysPerWeek: t.days_per_week,
      annualLeaveDays: Number(t.annual_leave_days),
      sickLeaveDays: Number(t.sick_leave_days),
      sickCycleMonths: t.sick_cycle_months,
      familyLeaveDays: Number(t.family_leave_days),
    });
  }

  const weekdays = (patternRes.data ?? []) as { weekday: number }[];
  // No roster recorded is not "works nothing": undefined means every rule that
  // reads it treats the day as one they would ordinarily work, which overpays a
  // public holiday rather than quietly taking it away.
  const worksWeekday = weekdays.length
    ? (weekday: number) => weekdays.some((w) => w.weekday === weekday)
    : undefined;

  const dayOf = dayLookup({ worksWeekday });
  const month0: RegisterMonth = {
    rows: [], hours: 0, overtime: 0, wages: 0, completed: 0, holidays: [],
  };

  for (const row of shiftRes.data ?? []) {
    const clockIn = Date.parse(row.clock_in);
    const date = zonedDate(clockIn, timeZone);
    if (!date.startsWith(prefix)) continue;

    const clockOut = row.clock_out ? Date.parse(row.clock_out) : null;
    const term = terms.get(row.terms_id);
    // splitHoursOn reads clockIn and clockOut; the rest of Shift is not consulted.
    const shift = { clockIn, clockOut } as Shift;
    // No work pattern is loaded here, so dayLookup's safe default applies: the
    // day counts as one this person ordinarily works, which overpays a public
    // holiday rather than underpaying it. Wire work_pattern in when the
    // register grows a per-person view.
    const day = dayOf(shift);
    const split = term
      ? splitHoursOn(shift, term, day)
      : { total: 0, ordinary: 0, overtime: 0, sunday: 0, holiday: 0 };

    const wages = term ? payForDay(shift, term, day).amount : 0;

    month0.rows.push({
      id: row.id,
      date,
      clockIn: zonedTime(clockIn, timeZone),
      clockOut: clockOut === null ? null : zonedTime(clockOut, timeZone),
      hours: split.total,
      overtime: split.overtime,
      wages,
      status: statusOf(row, split.overtime),
    });

    month0.hours += split.total;
    month0.overtime += split.overtime;
    month0.wages += wages;
    if (clockOut !== null) month0.completed += 1;
  }

  // Days the Act pays for with no shift behind them.
  const term = [...terms.values()]
    .sort((a, b) => b.validFrom.localeCompare(a.validFrom))
    .find((t) => t.validFrom <= last && (t.validTo === null || t.validTo >= last));

  if (term) {
    const worked = new Set(month0.rows.map((r) => r.date));
    const onLeave = new Set<string>();
    for (const r of (leaveRes.data ?? []) as { starts_on: string; ends_on: string }[]) {
      let d = new Date(`${r.starts_on}T00:00:00Z`);
      const end = new Date(`${r.ends_on}T00:00:00Z`);
      while (d <= end) {
        onLeave.add(d.toISOString().slice(0, 10));
        d = new Date(d.getTime() + 86_400_000);
      }
    }

    month0.holidays = unworkedHolidays({
      from: first, to: last, terms: term, worksWeekday,
      workedOn: (iso) => worked.has(iso),
      onLeave: (iso) => onLeave.has(iso),
      // A holiday that has not arrived is not owed yet, and nobody knows yet
      // whether it will be worked. Without this, the current month shows a
      // paid day for a Thursday that has not happened.
      asAt: zonedDate(Date.now(), timeZone),
    });

    // Counted into wages, because the payslip counts them. A register and a
    // payslip for the same month that disagree about the total is the one
    // thing neither document survives.
    for (const h of month0.holidays) month0.wages += h.amount;
  }

  return month0;
}
